import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, ChangeDetectorRef, inject, ViewChild, ElementRef, AfterViewInit, NgZone } from '@angular/core';
import { CategoriesService } from './services/categories.service';
import { Subscription, tap } from 'rxjs';
import { CategoryModel } from './models/category.model';
import { WindowService } from '@shared/window';
import Swiper from 'swiper';
import { Autoplay, FreeMode, Navigation, Pagination } from 'swiper/modules';
import { TranslateDirective } from '@ngx-translate/core';
import { CategoryThumbnailComponent } from './components/category-thumbnail/category-thumbnail.component';

@Component({
    selector: 'app-our-products',
    templateUrl: './our-products.component.html',
    styleUrls: ['./our-products.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CategoriesService],
    imports: [TranslateDirective, CategoryThumbnailComponent],
})
export class OurProductsComponent implements OnInit, AfterViewInit, OnDestroy {
    private zone = inject(NgZone);
    private categoriesService = inject(CategoriesService);
    private cdr = inject(ChangeDetectorRef);
    private windowService = inject(WindowService);

    @ViewChild('swiper') swiperRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperPagination') swiperPaginationRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperNavigationNext') swiperNavigationNextRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperNavigationPrev') swiperNavigationPrevRef!: ElementRef<HTMLDivElement>;

    readonly isClient = this.windowService.isClient;

    private swiper?: Swiper;

    categories?: CategoryModel[];

    private subscriptions$ = new Subscription();

    ngOnInit(): void {
        this.subscriptions$.add(
            this.categoriesService
                .getCategories$()
                .pipe(
                    tap((categories) => {
                        this.categories = categories;
                        this.cdr.detectChanges();
                        return categories;
                    }),
                )
                .subscribe(),
        );
    }

    ngAfterViewInit() {
        if (this.windowService.isBrowser) {
            this.initSwiper();
        }
    }

    ngOnDestroy(): void {
        this.swiper?.destroy();
    }

    private initSwiper() {
        this.zone.runOutsideAngular(() => {
            this.swiper = new Swiper(this.swiperRef.nativeElement, {
                modules: [Pagination, Navigation, Autoplay, FreeMode],
                spaceBetween: 26,
                pagination: {
                    el: this.swiperPaginationRef.nativeElement,
                    type: 'bullets',
                },
                navigation: {
                    prevEl: this.swiperNavigationPrevRef.nativeElement,
                    nextEl: this.swiperNavigationNextRef.nativeElement,
                },
                speed: 1500,
                autoplay: {
                    delay: 4000,
                    disableOnInteraction: false,
                },
                freeMode: {
                    enabled: true,
                    sticky: true,
                    momentum: false,
                },
                loop: true,
                shortSwipes: false,
                centeredSlides: true,
                watchOverflow: true,
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
                breakpoints: {
                    0: {
                        slidesPerView: 'auto',
                    },
                    370: {
                        slidesPerView: 2,
                        spaceBetween: 26,
                    },
                    768: {
                        slidesPerView: 3,
                        spaceBetween: 26,
                    },
                    1000: {
                        slidesPerView: 3,
                        spaceBetween: 26,
                    },
                    1200: {
                        slidesPerView: 6,
                        spaceBetween: 26,
                        centeredSlides: false,
                    },
                },
            });
        });
    }
}
